'use client';

import { type FC, useEffect, useMemo, useState } from 'react';
import type { GameV1 } from '@/api/games';
import { mapGameV2ToGameV1 } from '@/helpers/game';
import { SPECIAL_GAME_CATEGORY_SLIDER_PARAMS } from '@/helpers/games';
import { useGamesCurrencies } from '@/hooks/useGamesCurrencies';
import { useGetCommonStore } from '@/store';
import { combineSubCategoryGames, moveLeadingEmojisToEnd } from '../../helpers';
import { useCMSGameCategories } from '../../hooks';
import LobbyGamesSlider from '../LobbyGamesSlider';

const SPECIAL_GAME_SUB_CATEGORY_MIN_PAGE_SIZE = 23;

export const GameCategorySlider: FC = () => {
  const useCommonStore = useGetCommonStore();
  const fetchGames = useCommonStore.use.fetchGames();
  const currencies = useGamesCurrencies();

  const { categories } = useCMSGameCategories();

  const category = useMemo(
    () => categories.find(({ showOnLobby }) => showOnLobby) || null,
    [categories]
  );

  const title = useMemo(() => {
    if (!category || typeof category.title !== 'string') return '';
    return moveLeadingEmojisToEnd(category.title);
  }, [category]);

  const [gamesList, setGamesList] = useState<GameV1[]>([]);

  useEffect(() => {
    let isCancelled = false;

    try {
      if (!category) return;
      const identifiers = category.subCategories.map(
        ({ categoryId }) => categoryId
      );
      const promises = identifiers.map((identifier) =>
        fetchGames({
          currencies,
          filter: { categories: { identifiers: [identifier] } },
          pageSize: SPECIAL_GAME_SUB_CATEGORY_MIN_PAGE_SIZE,
        })
      );

      Promise.all(promises)
        .then((data) => {
          if (isCancelled) return;
          const filteredData = data.filter((item) => item !== undefined);
          setGamesList(
            mapGameV2ToGameV1(combineSubCategoryGames(filteredData))
          );
        })
        .catch((error) => {
          console.error(
            '[Promise.all] Error fetching games for GameCategorySlider:',
            error
          );
        });
    } catch (error) {
      console.error(
        '[try catch] Error fetching games for GameCategorySlider:',
        error
      );
    }

    return () => {
      isCancelled = true;
    };
  }, [category, currencies, fetchGames]);

  if (!category) return null;

  return (
    <LobbyGamesSlider
      categoryId={category.id}
      gamesList={category.subCategories.length ? gamesList : undefined}
      sliderParams={SPECIAL_GAME_CATEGORY_SLIDER_PARAMS}
      title={title}
    />
  );
};
